import type { AdminOrgDto } from '@carcheck/shared';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { companyErrorKey } from './companyErrors';
import { toCreateBody, type CompanyFormValues } from './companyForm';
import { logoErrorKey, uploadOrgLogo } from './logoUpload';
import { useOwnerCode, type UseOwnerCodeResult } from './useOwnerCode';
import { createOrg, useAdminOrgsCache } from '../../../api/admin-orgs';
import { publishToast } from '../../../components/feedback';
import { companyDetailPath } from '../../../routes/routes';

/**
 * The create-page sequence (AUT-403):
 *
 *   `POST /admin/orgs` → (a picked logo) presign / PUT / confirm →
 *   `POST /admin/orgs/:id/owner-code` → the one-time owner-code dialog.
 *
 * A failed logo does not undo the company: a warning toast, and the card's
 * «Заменить» retries. A failed owner-code issue goes straight to the card
 * (its «Коды» tab issues one). Closing the dialog clears the code and
 * opens the card.
 */
export interface UseCreateCompanyResult {
  busy: boolean;
  created: AdminOrgDto | null;
  ownerCode: UseOwnerCodeResult;
  submit: (values: CompanyFormValues, logo: File | null) => Promise<void>;
  closeOwnerCode: () => void;
}

const WARN_MS = 6000;

export const useCreateCompany = (): UseCreateCompanyResult => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const cache = useAdminOrgsCache();
  const ownerCode = useOwnerCode();
  const [created, setCreated] = useState<AdminOrgDto | null>(null);
  const [busy, setBusy] = useState(false);

  const uploadLogo = async (orgId: string, logo: File): Promise<void> => {
    try {
      await uploadOrgLogo(orgId, logo);
    } catch (err: unknown) {
      publishToast({
        severity: 'warning',
        durationMs: WARN_MS,
        message: `${t('companies.logo.createdWithoutLogo')} ${t(logoErrorKey(err))}`,
      });
    }
  };

  const submit = async (values: CompanyFormValues, logo: File | null): Promise<void> => {
    setBusy(true);
    try {
      const org = await createOrg(toCreateBody(values));
      setCreated(org);
      if (logo) await uploadLogo(org.id, logo);
      await cache.invalidate();
      if (!(await ownerCode.issue(org.id))) navigate(companyDetailPath(org.id));
    } catch (err: unknown) {
      publishToast({ severity: 'error', durationMs: WARN_MS, message: t(companyErrorKey(err)) });
    } finally {
      setBusy(false);
    }
  };

  const closeOwnerCode = (): void => {
    ownerCode.clear();
    if (created) navigate(companyDetailPath(created.id));
  };

  return { busy, created, ownerCode, submit, closeOwnerCode };
};
